import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";
import editaisPublicosService from "../../../services/editaisPublicosService";
import minhasInscricoesService from "../../../services/minhasInscricoesService";
import PlanilhaOrcamentaria from "../../Shared/PlanilhaOrcamentaria";
import type { DadosOrcamento, ConfigOrcamento } from "../../../types/orcamento";

interface FormularioEdital {
  edital: {
    id: number;
    nome: string;
    descricao: string;
    data_fim: string;
  };
  schema_campos: Record<
    string,
    {
      type: string;
      label: string;
      required?: boolean;
      placeholder?: string;
      maxLength?: number;
      min?: number;
      max?: number;
      default?: string | number;
      options?: string[];
      config?: ConfigOrcamento; // For orcamento field type
    }
  >;
  configuracoes?: Record<string, unknown>;
}

export default function FormInscricao() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const editalId = searchParams.get("edital_id");

  const [formulario, setFormulario] = useState<FormularioEdital | null>(null);
  const [formData, setFormData] = useState<Record<string, unknown>>({});
  const [loading, setLoading] = useState(true);
  const [salvando, setSalvando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [validacaoAtiva, setValidacaoAtiva] = useState(false); // Para controlar validação

  useEffect(() => {
    const carregarEdital = async () => {
      if (!editalId) {
        setError("ID do edital não fornecido");
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        console.log("Carregando formulário do edital:", editalId);
        const response = await editaisPublicosService.obterFormulario(
          parseInt(editalId)
        );
        console.log("Resposta do backend:", response);
        console.log("Schema campos:", response.schema_campos);
        setFormulario(response);

        // Inicializar formData com valores vazios
        const initialData: Record<string, unknown> = {};
        if (response.schema_campos) {
          Object.keys(response.schema_campos).forEach((key) => {
            initialData[key] = "";
          });
        }
        setFormData(initialData);
      } catch (err) {
        console.error("Erro detalhado:", err);
        const errorMessage =
          (err as { response?: { data?: { error?: string } } })?.response?.data
            ?.error || "Erro ao carregar formulário do edital";
        setError(errorMessage);
      } finally {
        setLoading(false);
      }
    };

    void carregarEdital();
  }, [editalId]);

  const handleChange = (campoId: string, value: unknown) => {
    setFormData((prev) => ({
      ...prev,
      [campoId]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!editalId || !formulario) return;

    // Validar campos obrigatórios
    const camposObrigatorios = Object.entries(formulario.schema_campos)
      .filter(([, campo]) => campo.required)
      .map(([key]) => key);

    const camposVazios = camposObrigatorios.filter((key) => {
      const valor = formData[key];
      const campo = formulario.schema_campos[key];
      
      // Para campos de orçamento, verificar se há itens
      if (campo.type === 'orcamento') {
        const orcamentoData = valor as DadosOrcamento | undefined;
        return !orcamentoData || !orcamentoData.itens || orcamentoData.itens.length === 0;
      }
      
      // Para outros tipos de campos
      return !valor || (typeof valor === "string" && valor.trim() === "");
    });

    if (camposVazios.length > 0) {
      const camposNomes = camposVazios
        .map((key) => formulario.schema_campos[key].label)
        .join(", ");
      setError(`Por favor, preencha os campos obrigatórios: ${camposNomes}`);
      setValidacaoAtiva(true);
      
      // Scroll para o primeiro campo com erro
      setTimeout(() => {
        const primeiroErro = document.querySelector('.border-red-500, .text-red-600');
        if (primeiroErro) {
          primeiroErro.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 100);
      
      return;
    }

    try {
      setSalvando(true);
      setError(null);

      await minhasInscricoesService.criar({
        edital_id: parseInt(editalId),
        dados_formulario: formData,
      });

      toast.success(
        "Inscrição submetida com sucesso! Aguarde a análise do administrador."
      );
      navigate("/inscricoes");
    } catch (err) {
      console.error("Erro ao submeter:", err);
      const responseData = (err as { response?: { data?: { error?: string; errors?: string[] } } })?.response?.data;
      
      let errorMessage = "Erro ao submeter inscrição";
      if (responseData?.errors && Array.isArray(responseData.errors)) {
        errorMessage = responseData.errors.join(", ");
      } else if (responseData?.error) {
        errorMessage = responseData.error;
      }
      
      setError(errorMessage);
      toast.error(errorMessage);
      
      // Scroll para o topo para ver a mensagem de erro
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setSalvando(false);
    }
  };

  const renderCampo = (
    key: string,
    campo: FormularioEdital["schema_campos"][string]
  ) => {
    const value = formData[key];
    
    // Validação específica por tipo de campo
    let mostrarErro = false;
    if (validacaoAtiva && campo.required) {
      if (campo.type === 'orcamento') {
        const orcamentoData = value as DadosOrcamento | undefined;
        mostrarErro = !orcamentoData || !orcamentoData.itens || orcamentoData.itens.length === 0;
      } else {
        mostrarErro = !value || (typeof value === "string" && value.trim() === "");
      }
    }

    switch (campo.type) {
      case "text":
        return (
          <>
            <input
              type="text"
              value={(value as string) || ""}
              onChange={(e) => handleChange(key, e.target.value)}
              placeholder={campo.placeholder || ""}
              maxLength={campo.maxLength}
              className={`w-full px-3 py-2 border rounded-md focus:ring-blue-500 focus:border-blue-500 ${
                mostrarErro ? "border-red-500" : "border-gray-300"
              }`}
            />
            {mostrarErro && (
              <p className="mt-1 text-sm text-red-600">
                Este campo é obrigatório
              </p>
            )}
          </>
        );

      case "textarea":
        return (
          <>
            <textarea
              value={(value as string) || ""}
              onChange={(e) => handleChange(key, e.target.value)}
              placeholder={campo.placeholder || ""}
              rows={4}
              maxLength={campo.maxLength}
              className={`w-full px-3 py-2 border rounded-md focus:ring-blue-500 focus:border-blue-500 ${
                mostrarErro ? "border-red-500" : "border-gray-300"
              }`}
            />
            {mostrarErro && (
              <p className="mt-1 text-sm text-red-600">
                Este campo é obrigatório
              </p>
            )}
          </>
        );

      case "number":
        return (
          <>
            <input
              type="number"
              value={(value as number) || ""}
              onChange={(e) =>
                handleChange(key, parseFloat(e.target.value) || 0)
              }
              placeholder={campo.placeholder || ""}
              min={campo.min}
              max={campo.max}
              className={`w-full px-3 py-2 border rounded-md focus:ring-blue-500 focus:border-blue-500 ${
                mostrarErro ? "border-red-500" : "border-gray-300"
              }`}
            />
            {mostrarErro && (
              <p className="mt-1 text-sm text-red-600">
                Este campo é obrigatório
              </p>
            )}
          </>
        );

      case "currency":
        return (
          <>
            <div className="relative">
              <span className="absolute left-4 top-3.5 lg:top-4 text-gray-500 font-medium">R$</span>
              <input
                type="number"
                value={(value as number) || ""}
                onChange={(e) =>
                  handleChange(key, parseFloat(e.target.value) || 0)
                }
                placeholder={campo.placeholder || "0,00"}
                min={campo.min}
                max={campo.max}
                step="0.01"
                className={`w-full pl-12 pr-4 py-3 lg:py-3.5 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${
                  mostrarErro ? "border-red-500" : "border-gray-300"
                }`}
              />
            </div>
            {mostrarErro && (
              <p className="mt-2 text-sm text-red-600">
                Este campo é obrigatório
              </p>
            )}
          </>
        );

      case "select":
        return (
          <>
            <select
              value={(value as string) || ""}
              onChange={(e) => handleChange(key, e.target.value)}
              className={`w-full px-4 py-3 lg:py-3.5 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${
                mostrarErro ? "border-red-500" : "border-gray-300"
              }`}
            >
              <option value="">Selecione uma opção</option>
              {campo.options?.map((opt, idx) => (
                <option key={idx} value={opt}>
                  {opt}
                </option>
              ))}
            </select>
            {mostrarErro && (
              <p className="mt-2 text-sm text-red-600">
                Este campo é obrigatório
              </p>
            )}
          </>
        );

      case "date":
        return (
          <>
            <input
              type="date"
              value={(value as string) || ""}
              onChange={(e) => handleChange(key, e.target.value)}
              className={`w-full px-4 py-3 lg:py-3.5 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${
                mostrarErro ? "border-red-500" : "border-gray-300"
              }`}
            />
            {mostrarErro && (
              <p className="mt-2 text-sm text-red-600">
                Este campo é obrigatório
              </p>
            )}
          </>
        );

      case "url":
        return (
          <>
            <input
              type="url"
              value={(value as string) || ""}
              onChange={(e) => handleChange(key, e.target.value)}
              placeholder={campo.placeholder || "https://"}
              className={`w-full px-4 py-3 lg:py-3.5 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${
                mostrarErro ? "border-red-500" : "border-gray-300"
              }`}
            />
            {mostrarErro && (
              <p className="mt-2 text-sm text-red-600">
                Este campo é obrigatório
              </p>
            )}
          </>
        );

      case "orcamento": {
        // Parse config from campo
        const config = (campo as any).config as ConfigOrcamento | undefined;
        const orcamentoData = (value as DadosOrcamento) || {
          itens: [],
          resumo: {
            total_geral: 0,
            totais_por_categoria: {} as Record<string, number>,
          },
          observacoes: "",
        };

        return (
          <>
            <PlanilhaOrcamentaria
              value={orcamentoData}
              onChange={(data) => handleChange(key, data)}
              config={config || {
                categorias: [
                  { value: 'pessoal', label: 'Pessoal' },
                  { value: 'material', label: 'Material de Consumo' },
                  { value: 'equipamento', label: 'Equipamentos' },
                  { value: 'servico', label: 'Serviços de Terceiros' },
                  { value: 'diaria', label: 'Diárias' },
                  { value: 'outros', label: 'Outros' },
                ],
              }}
              readonly={false}
            />
            {mostrarErro && (
              <p className="mt-2 text-sm text-red-600">
                Este campo é obrigatório
              </p>
            )}
          </>
        );
      }

      default:
        return (
          <>
            <input
              type="text"
              value={(value as string) || ""}
              onChange={(e) => handleChange(key, e.target.value)}
              placeholder={campo.placeholder || ""}
              className={`w-full px-4 py-3 lg:py-3.5 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${
                mostrarErro ? "border-red-500" : "border-gray-300"
              }`}
            />
            {mostrarErro && (
              <p className="mt-2 text-sm text-red-600">
                Este campo é obrigatório
              </p>
            )}
          </>
        );
    }
  };

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          <p className="mt-4 text-gray-600">Carregando formulário...</p>
        </div>
      </div>
    );
  }

  if (!formulario) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">{error || "Formulário não encontrado"}</p>
        </div>
        <button
          onClick={() => navigate(-1)}
          className="mt-4 text-blue-600 hover:text-blue-800"
        >
          ← Voltar
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6 lg:py-10">
        {/* Botão Voltar */}
        <div className="mb-8">
          <button
            onClick={() => navigate(-1)}
            className="text-gray-600 hover:text-gray-900 transition font-medium"
          >
            ← Voltar
          </button>
        </div>

        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl lg:text-4xl font-bold text-gray-900">Nova Inscrição</h1>
          <p className="mt-3 text-gray-600 text-base lg:text-lg">
            Preencha o formulário abaixo para se inscrever
          </p>
        </div>

        {/* Informações do Edital */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-5 lg:p-6 mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-3">
            <h3 className="text-lg lg:text-xl font-semibold text-blue-900">
              {formulario.edital.nome}
            </h3>
            <span className="text-xs bg-blue-100 text-blue-800 px-3 py-1.5 rounded-lg font-medium w-fit">
              Edital #{formulario.edital.id}
            </span>
          </div>
          <p className="text-sm lg:text-base text-blue-700">
            Prazo de inscrição: até{" "}
            {new Date(formulario.edital.data_fim).toLocaleDateString("pt-BR", {
              day: "2-digit",
              month: "long",
              year: "numeric",
            })}
          </p>
        </div>

        {/* Formulário */}
        <form onSubmit={handleSubmit} className="space-y-8">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 lg:p-8">
            <h2 className="text-xl lg:text-2xl font-bold text-gray-900 mb-8">
              Formulário de Inscrição
            </h2>
            <div className="space-y-8">
            {Object.entries(formulario.schema_campos).map(([key, campo]) => (
              <div key={key}>
                <label className="block text-sm lg:text-base font-medium text-gray-700 mb-2.5">
                  {campo.label}
                  {campo.required && (
                    <span className="text-red-500 ml-1">*</span>
                  )}
                </label>
                {renderCampo(key, campo)}

                {/* Info do campo */}
                {(campo.maxLength ||
                  campo.min !== undefined ||
                  campo.max !== undefined) && (
                  <div className="mt-2 flex flex-wrap gap-3 text-xs lg:text-sm text-gray-500">
                    {campo.maxLength && (
                      <span>• Máximo: {campo.maxLength} caracteres</span>
                    )}
                    {campo.min !== undefined && (
                      <span>• Mínimo: {campo.min}</span>
                    )}
                    {campo.max !== undefined && (
                      <span>• Máximo: {campo.max}</span>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Error */}
        {error && (
          <div className="bg-red-50 border-l-4 border-red-500 rounded-lg p-5 shadow-sm">
            <div className="flex items-start gap-3">
              <svg className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
              <div className="flex-1">
                <h4 className="text-red-800 font-semibold mb-1">Erro ao submeter inscrição</h4>
                <p className="text-red-700 text-sm lg:text-base">{error}</p>
              </div>
              <button 
                onClick={() => setError(null)}
                className="text-red-400 hover:text-red-600 transition"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>
            </div>
          </div>
        )}

        {/* Botão Submeter */}
        <div className="flex justify-end">
          <button
            type="submit"
            disabled={salvando}
            className="bg-blue-600 text-white px-8 lg:px-10 py-3 lg:py-4 rounded-lg hover:bg-blue-700 transition disabled:opacity-50 font-semibold text-base lg:text-lg"
          >
            {salvando ? "Submetendo..." : "Submeter Inscrição"}
          </button>
        </div>
      </form>
      </div>
    </div>
  );
}
